import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import debounce from "lodash.debounce";
import { gaEvent } from "@/lib/ga";
import {
  validateEmail,
  validatePassword,
  validateUsername,
} from "@/util/validate";
import { setLoadingBar } from "@/redux/siteData";
import { api } from "@/services/api";
import { errorCodes } from "@chesshotel/constants";
import {
  useRegister,
  setUsername,
  setEmail,
  setPassword,
  setNews,
  setStep,
  setSignedUpWithEmail,
  setSocialToken,
  setSocialAccountType,
  setUsernameError,
  setEmailError,
  setPasswordError,
  RegisterStep,
} from "@/redux/register";
import { setUserData, setLoading } from "@/redux/signIn";
import {
  inlineSignInOrWillCreateGoogleUser as inlineSignInOrWillCreateGoogleUserAction,
  inlineSignInOrWillCreateFB as inlineSignInOrWillCreateFBAction,
  signInFBInline as signInFBInlineAction,
  inlineSignInOrWillCreateAppleUser as inlineSignInOrWillCreateAppleUserAction,
  signInAppleWebInline as signInAppleWebInlineAction,
  initializeGoogleButton as initializeGoogleButtonAction,
} from "./registerActions";

export type { RegisterStep };

export interface UseRegisterFormOptions {
  onStepChange?: (step: RegisterStep) => void;
  onExistingUserSignIn?: () => void;
}

export function useRegisterForm(options: UseRegisterFormOptions = {}) {
  const { onStepChange, onExistingUserSignIn } = options;
  const router = useRouter();
  const {
    guestElo,
    progressKey,
    guestUsername,
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    socialToken,
    socialAccountType,
    usernameError,
    emailError,
    passwordError,
  } = useRegister();

  // Track if we've already processed the redirect from sign-in
  const hasProcessedRedirect = useRef(false);
  const latestEmailRef = useRef(email);

  useEffect(() => {
    latestEmailRef.current = email;
  }, [email]);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Internal step setter that also notifies parent
  const updateStep = useCallback(
    (newStep: RegisterStep) => {
      setStep(newStep);
      onStepChange?.(newStep);
    },
    [onStepChange, setStep]
  );

  // Initialize
  useEffect(() => {
    if (guestUsername) {
      validateUsername(guestUsername, router.locale);
    }
    gaEvent({ category: "RegisterWithEmail", action: "StartCreateAccount" });
  }, []);

  // Handle redirect from sign-in page when user doesn't exist
  useEffect(() => {
    // Only process once and if we're still on the form step
    if (hasProcessedRedirect.current || step !== "form") return;

    const { accountType, credential } = router.query;
    if (
      accountType === "google" &&
      credential &&
      typeof credential === "string"
    ) {
      // User was redirected from sign-in because they don't have an account
      // We already know the user doesn't exist (got 204 from backend)
      // So we can directly proceed to username step without calling backend again
      hasProcessedRedirect.current = true;
      setSocialToken(credential);
      setSocialAccountType("google");
      updateStep("socialUsername");

      // Clean up the query params from URL
      router.replace("/register", undefined, { shallow: true });
    }
  }, [router.query, step]);

  // Validation functions
  const debouncedValidateUsername = useCallback(
    debounce(async (val: string) => {
      setUsernameError(await validateUsername(val, router.locale));
    }, 700),
    [router.locale]
  );

  const debouncedValidatePassword = useCallback(
    debounce(async (val: string) => {
      setPasswordError(validatePassword(val));
    }, 700),
    []
  );

  // Form change handlers
  function emailChange(val: string) {
    latestEmailRef.current = val;
    setEmail(val);
    setEmailError(null);
  }

  async function emailBlur(val: string) {
    const error = await validateEmail(val);
    if (latestEmailRef.current === val) {
      setEmailError(error);
    }
  }

  async function usernameChange(val: string) {
    setUsername(val);
    debouncedValidateUsername(val);
  }

  function passwordChange(val: string) {
    setPassword(val);
    debouncedValidatePassword(val);
  }

  // Account creation
  async function validateCreateAccount() {
    const passwordErr = validatePassword(password);
    const emailErr = await validateEmail(email);
    const usernameErr = await validateUsername(username, router.locale);

    setPasswordError(passwordErr);
    setEmailError(emailErr);
    setUsernameError(usernameErr);

    if (!passwordErr && !emailErr && !usernameErr) {
      createAccountRequest();
    }
  }

  function createAccountRequest() {
    setLoadingBar(true);

    const lang = router.locale;
    const elo = guestElo;
    const buildTarget = "web";

    api
      .post(
        "/users",
        {
          username,
          email,
          password,
          lang,
          country: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          elo,
          news,
          buildTarget,
          progressKey,
        },
        {
          validateStatus: function (status) {
            return status < 400;
          },
        }
      )
      .then((response) => {
        if (response.data) {
          setSignedUpWithEmail(true);
          updateStep("joined");
          gaEvent({ category: "RegisterWithEmail", action: "CreateAccount" });
          setUserData(response.data);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .catch((error) => {
        if (error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else if (
          error?.response?.data === errorCodes.ERROR_EMAIL_NOT_AVAILABLE
        ) {
          setEmailError(errorCodes.ERROR_EMAIL_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  async function advanceToUsernameStep() {
    const passwordErr = validatePassword(password);
    const emailErr = await validateEmail(email);
    setPasswordError(passwordErr);
    setEmailError(emailErr);
    if (!passwordErr && !emailErr) {
      updateStep("username");
    }
  }

  // Social sign-in handlers - wrap actions with callbacks
  const callbacks = useCallback(
    () => ({
      onExistingUserSignIn,
      onStepChange: updateStep,
    }),
    [onExistingUserSignIn, updateStep]
  );

  function inlineSignInOrWillCreateFB(accessToken: string) {
    inlineSignInOrWillCreateFBAction(accessToken, callbacks());
  }

  function signInFBInline() {
    signInFBInlineAction(callbacks());
  }

  function inlineSignInOrWillCreateAppleUser(token: string) {
    inlineSignInOrWillCreateAppleUserAction(token, callbacks());
  }

  async function signInAppleWebInline() {
    await signInAppleWebInlineAction(callbacks());
  }

  function inlineSignInOrWillCreateGoogleUser(credential: string) {
    inlineSignInOrWillCreateGoogleUserAction(credential, callbacks());
  }

  const initializeGoogleButton = useCallback(() => {
    initializeGoogleButtonAction(step, callbacks());
  }, [step, callbacks]);

  useEffect(() => {
    // Only initialize Google button when we're on the form step
    if (step !== "form") return;

    // Initialize Google button when component mounts or when navigating to this page
    // Check if Google script is already loaded
    if ((window as any).google?.accounts?.id) {
      initializeGoogleButton();
    } else {
      // If not loaded yet, wait a bit and try again
      const checkInterval = setInterval(() => {
        if ((window as any).google?.accounts?.id) {
          clearInterval(checkInterval);
          initializeGoogleButton();
        }
      }, 100);

      // Cleanup after 5 seconds if script doesn't load
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, [step, router.locale, initializeGoogleButton]);

  async function createSocialAccount() {
    // Validate then create social user
    const err = await validateUsername(username, router.locale);
    setUsernameError(err);
    if (err) return;
    if (!socialToken || !socialAccountType) return;
    setLoadingBar(true);
    let request;
    if (socialAccountType === "facebook") {
      request = api.post("/users/facebook", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else if (socialAccountType === "apple") {
      request = api.post("/users/apple", {
        username,
        token: socialToken,
        progressKey,
        lang: router.locale,
      });
    } else {
      request = api.post("/auth/google/token", {
        credential: socialToken,
        username,
        progressKey,
        lang: router.locale,
        elo: guestElo,
      });
    }
    request
      .then((res) => {
        // Check for successful response (200 or 201) with data
        if (res.status === 200 && res?.data) {
          setSignedUpWithEmail(false);
          setUserData(res.data);
          setLoading(false);
          updateStep("joined");
        } else if (res.status === 200 && !res?.data) {
          console.warn("Response 200 but no data - might be an issue");
          // Still try to proceed if status is 200
          setLoading(false);
          updateStep("joined");
        }
      })
      .catch((error) => {
        console.error("createSocialAccount error:", error);
        console.error("error?.response:", error?.response);
        if (error?.response?.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => setLoadingBar(false));
  }

  const failStyle = { border: "1px solid #ff4900" } as const;
  const usernameStyle = usernameError && failStyle;
  const emailStyle = emailError && failStyle;
  const passwordStyle = passwordError && failStyle;

  return {
    // State
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    usernameError,
    emailError,
    passwordError,
    usernameStyle,
    emailStyle,
    passwordStyle,
    guestElo,
    // Handlers
    setNews,
    emailChange,
    emailBlur,
    usernameChange,
    passwordChange,
    advanceToUsernameStep,
    validateCreateAccount,
    createSocialAccount,
    signInFBInline,
    signInAppleWebInline,
    initializeGoogleButton,
    inlineSignInOrWillCreateGoogleUser,
  };
}
